import { API_CONFIG, buildApiUrl, ensureSecureRequestUrl, getApiBaseUrl, getAuthHeaders } from '../config/api';
import {
  SystemStatus,
  PerformanceMetrics,
  TradingAlert,
  WebhookMapping,
  AlertConfiguration,
  TriggerStatusResponse,
  IncomingWebhookActivityRow,
} from '../types/dashboard';
import {
  formatRecentAlertMessage,
  MISSING_TRIGGER_WEBHOOK_MESSAGE,
} from '../utils/alertUserFacingText';

// API Response types
interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  error?: string;
}

// Error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Map AlertLog API rows to TradingAlert (dashboard feed). */
function mapAlertLogsToTradingAlerts(logs: unknown[]): TradingAlert[] {
  if (!Array.isArray(logs)) return [];
  return logs.map((log) => {
    const r = log as Record<string, unknown>;
    const st = String(r.execution_status ?? '').toLowerCase();
    const status: TradingAlert['status'] =
      st === 'success' ? 'success' :
      st === 'error' ? 'error' :
      st === 'low_confidence' ? 'low_confidence' : 'pending';
    const ms = r.execution_time_ms;
    const execSec = typeof ms === 'number' && ms > 0 ? ms / 1000 : 0;
    const created = r.created_at;
    const alertIdRaw = r.alert_id ?? r.alertId;
    const alertConfigId =
      typeof alertIdRaw === 'number'
        ? alertIdRaw
        : typeof alertIdRaw === 'string' && /^\d+$/.test(alertIdRaw)
          ? parseInt(alertIdRaw, 10)
          : undefined;
    return {
      id: String(r.id ?? ''),
      stockSymbol: String(r.ticker ?? ''),
      positionType: String(r.order_type ?? ''),
      status,
      tradeSide: String(r.trade_side ?? r.tradeSide ?? '').trim() || undefined,
      timestamp: created instanceof Date ? created.toISOString() : String(created ?? ''),
      executionTime: execSec,
      confidenceScore: typeof r.confidence_score === 'number' ? Math.round(r.confidence_score) : 0,
      webhookUrl: String(r.webhook_url ?? r.webhookUrl ?? ''),
      secretWord: '',
      errorMessage:
        formatRecentAlertMessage(
          String(r.error_message ?? r.errorMessage ?? '').trim() || undefined,
          status
        ) ?? undefined,
      alertConfigId,
    };
  });
}

/** Extract human-readable message from FastAPI error JSON ({ detail: ... }). */
export function formatApiErrorDetail(response: unknown): string | null {
  if (!response || typeof response !== 'object') return null;
  const d = (response as { detail?: unknown }).detail;
  if (typeof d === 'string' && d.trim()) return d.trim();
  if (Array.isArray(d)) {
    const parts = d
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg?: unknown }).msg ?? '');
        }
        return '';
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : null;
  }
  return null;
}

/** Browser session marker. Not a JWT and never sent as Authorization. */
export const COOKIE_SESSION = 'cookie-session';

let csrfTokenMemory: string | null = null;

function rememberCsrf(response: Response): void {
  const header = response.headers.get('X-CSRF-Token');
  if (header) {
    csrfTokenMemory = header;
    return;
  }
  if (typeof document === 'undefined') return;
  const match = document.cookie.match(/(?:^|; )br_csrf=([^;]*)/);
  if (match) csrfTokenMemory = decodeURIComponent(match[1]);
}

function csrfHeaders(): Record<string, string> {
  if (!csrfTokenMemory && typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|; )br_csrf=([^;]*)/);
    if (match) csrfTokenMemory = decodeURIComponent(match[1]);
  }
  return csrfTokenMemory ? { 'X-CSRF-Token': csrfTokenMemory } : {};
}

/** Real JWTs have three segments. Session markers and empty values are ignored. */
function bearerToken(token?: string | null): string | null {
  if (!token || token === COOKIE_SESSION) return null;
  return token.split('.').length === 3 ? token : null;
}

// API Service class
class ApiService {
  private baseUrl: string;
  private timeout: number;
  /** In-memory token synced from AuthContext so requests always use current auth. */
  private authToken: string | null = null;
  private refreshInFlight: Promise<string | null> | null = null;
  private onSessionExpired: (() => void) | null = null;

  formatErrorDetail(error: ApiError): string | null {
    return formatApiErrorDetail(error.response);
  }

  constructor() {
    this.baseUrl = getApiBaseUrl();
    this.timeout = API_CONFIG.TIMEOUT;
  }

  /** Set auth token from AuthContext (login/init). Clear on logout/401. */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  /** AuthContext registers handler to clear session when refresh fails. */
  setSessionExpiredHandler(handler: (() => void) | null): void {
    this.onSessionExpired = handler;
  }

  setRefreshToken(token: string | null): void {
    // Refresh tokens live in an HttpOnly cookie. Drop any legacy copy.
    localStorage.removeItem('refreshToken');
    void token;
  }

  private getStoredTenantId(): string {
    try {
      const raw = localStorage.getItem('tenant');
      if (raw) {
        const t = JSON.parse(raw) as { id?: string };
        if (t?.id) return String(t.id);
      }
    } catch {
      /* ignore */
    }
    return 'default';
  }

  /** Bearer token only when the caller still holds a real JWT (desktop-style). */
  private getAuthToken(): string | null {
    return bearerToken(this.authToken ?? localStorage.getItem('authToken'));
  }

  private async refreshAccessToken(): Promise<string | null> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = (async () => {
      try {
        const legacy = bearerToken(localStorage.getItem('refreshToken'));
        const url = ensureSecureRequestUrl(buildApiUrl(API_CONFIG.ENDPOINTS.REFRESH));
        const response = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            ...API_CONFIG.DEFAULT_HEADERS,
            ...csrfHeaders(),
            'X-Tenant-ID': this.getStoredTenantId(),
          },
          body: JSON.stringify(legacy ? { refresh_token: legacy } : {}),
        });
        rememberCsrf(response);
        if (!response.ok) {
          return null;
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        this.setAuthToken(null);
        window.dispatchEvent(
          new CustomEvent('branchrouter:token-refreshed', { detail: { accessToken: COOKIE_SESSION } })
        );
        return COOKIE_SESSION;
      } catch {
        return null;
      } finally {
        this.refreshInFlight = null;
      }
    })();

    return this.refreshInFlight;
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    options: RequestInit & { _authRetry?: boolean } = {}
  ): Promise<ApiResponse<T>> {
    const url = ensureSecureRequestUrl(buildApiUrl(endpoint));
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      // Attach auth token to all requests (in-memory from AuthContext, then localStorage).
      // Merge auth last so Authorization is never overwritten by caller options.
      // Build headers as Record<string, string> so TypeScript accepts as HeadersInit.
      const token = this.getAuthToken();
      const incoming = options.headers;
      const normalized: Record<string, string> =
        incoming instanceof Headers
          ? Object.fromEntries(incoming.entries())
          : Array.isArray(incoming)
            ? Object.fromEntries(incoming as [string, string][])
            : (incoming as Record<string, string>) || {};
      const headers: Record<string, string> = {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...normalized,
        ...csrfHeaders(),
        'X-Tenant-ID': this.getStoredTenantId(),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      const presented = headers.Authorization || headers.authorization;
      if (presented && !bearerToken(presented.replace(/^Bearer\s+/i, ''))) {
        delete headers.Authorization;
        delete headers.authorization;
      }
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        signal: controller.signal,
        headers,
      });
      rememberCsrf(response);

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        let errorData: unknown = null;
        if (errorText && errorText.trim()) {
          try {
            errorData = JSON.parse(errorText);
          } catch {
            errorData = { detail: errorText };
          }
        }

        if (
          response.status === 401 &&
          !options._authRetry &&
          endpoint !== API_CONFIG.ENDPOINTS.LOGIN &&
          endpoint !== API_CONFIG.ENDPOINTS.REGISTER &&
          endpoint !== API_CONFIG.ENDPOINTS.REFRESH &&
          endpoint !== API_CONFIG.ENDPOINTS.VERIFY_EMAIL &&
          endpoint !== API_CONFIG.ENDPOINTS.RESEND_VERIFICATION &&
          endpoint !== API_CONFIG.ENDPOINTS.PASSWORD_RESET &&
          endpoint !== API_CONFIG.ENDPOINTS.PASSWORD_RESET_CONFIRM
        ) {
          const newToken = await this.refreshAccessToken();
          if (newToken) {
            return this.request<T>(endpoint, { ...options, _authRetry: true });
          }
          this.setAuthToken(null);
          this.setRefreshToken(null);
          this.onSessionExpired?.();
        }

        throw new ApiError(
          formatApiErrorDetail(errorData) ||
            `HTTP ${response.status}: ${response.statusText}`,
          response.status,
          errorData
        );
      }

      // DELETE/204 or proxies may return an empty body; response.json() would throw.
      if (response.status === 204 || response.status === 205) {
        return { data: undefined as unknown as T, success: true };
      }
      const rawText = await response.text();
      if (!rawText || !rawText.trim()) {
        return { data: undefined as unknown as T, success: true };
      }
      const ct = response.headers.get('content-type') || '';
      const data = ct.includes('application/json')
        ? (JSON.parse(rawText) as T)
        : (rawText as unknown as T);
      return {
        data,
        success: true,
      };
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout - Backend may not be running', 408);
      }
      
      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const base = getApiBaseUrl() || (typeof window !== 'undefined' ? window.location.origin : 'this app origin');
        throw new ApiError(
          `Cannot connect to API (base: ${base || 'relative /api'}). For production behind nginx, rebuild with empty REACT_APP_API_URL so calls use the same host/port as the page.`,
          0
        );
      }
      
      throw new ApiError(
        error instanceof Error ? error.message : 'Unknown error occurred',
        0
      );
    }
  }

  // Generic HTTP methods
  async get<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  async put<T>(endpoint: string, data?: any, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  }

  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // Health and status endpoints
  async getHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request(API_CONFIG.ENDPOINTS.HEALTH);
  }

  async getSystemStatus(): Promise<ApiResponse<SystemStatus>> {
    return this.request(API_CONFIG.ENDPOINTS.SYSTEM_STATUS);
  }

  async getStatus(): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.STATUS);
  }

  // Authentication endpoints
  async login(email: string, password: string): Promise<ApiResponse<{ access_token: string; refresh_token: string; token_type: string; expires_in: number; user: any }>> {
    return this.request(API_CONFIG.ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async logout(): Promise<ApiResponse<void>> {
    return this.request(API_CONFIG.ENDPOINTS.LOGOUT, {
      method: 'POST',
    });
  }

  async refreshSession(): Promise<ApiResponse<{ access_token: string }>> {
    const access = await this.refreshAccessToken();
    if (!access) {
      throw new ApiError('Session expired', 401);
    }
    return { success: true, data: { access_token: access } };
  }

  async getCurrentUser(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ME, {
      headers: getAuthHeaders(token),
    });
  }

  /** Request a password reset email for the given address (unauthenticated). */
  async requestPasswordReset(
    email: string
  ): Promise<ApiResponse<{ message: string; reset_token_sent: boolean; reset_token?: string }>> {
    return this.request(API_CONFIG.ENDPOINTS.PASSWORD_RESET, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /** Complete a password reset using the token from the reset email (unauthenticated). */
  async confirmPasswordReset(
    resetToken: string,
    newPassword: string
  ): Promise<ApiResponse<{ message: string; success: boolean }>> {
    return this.request(API_CONFIG.ENDPOINTS.PASSWORD_RESET_CONFIRM, {
      method: 'POST',
      body: JSON.stringify({ reset_token: resetToken, new_password: newPassword }),
    });
  }

  /** Confirm an email address using the token from the verification email (unauthenticated). */
  async verifyEmail(
    verificationToken: string
  ): Promise<ApiResponse<{ message: string; verified?: boolean; success?: boolean }>> {
    return this.request(API_CONFIG.ENDPOINTS.VERIFY_EMAIL, {
      method: 'POST',
      body: JSON.stringify({ verification_token: verificationToken }),
    });
  }

  /** Request a new account email-verification link (unauthenticated; response is always generic). */
  async resendVerificationEmail(
    email: string
  ): Promise<ApiResponse<{ message: string; sent?: boolean }>> {
    return this.request(API_CONFIG.ENDPOINTS.RESEND_VERIFICATION, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  /** Change the password for the logged-in user (verifies the current password). */
  async changePassword(
    currentPassword: string,
    newPassword: string,
    token?: string
  ): Promise<ApiResponse<{ message: string; success: boolean }>> {
    return this.request(API_CONFIG.ENDPOINTS.CHANGE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Re-verify password and receive a short-lived step-up token for payload reveal. */
  async reauthenticate(
    password: string,
    token?: string
  ): Promise<ApiResponse<{ step_up_token: string; token_type: string; expires_in: number; message: string }>> {
    return this.request(API_CONFIG.ENDPOINTS.REAUTHENTICATE, {
      method: 'POST',
      body: JSON.stringify({ password }),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Trading alerts endpoints
  async getAlerts(token?: string): Promise<ApiResponse<TradingAlert[]>> {
    return this.request(API_CONFIG.ENDPOINTS.ALERTS, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /**
   * Recent alert execution logs (AlertLog). Backend: GET /api/v1/alerts/?skip=&limit=
   * (there is no /alerts/history route).
   */
  async getAlertHistory(
    page: number = 1,
    limit: number = 20,
    token?: string
  ): Promise<ApiResponse<TradingAlert[]>> {
    const skip = Math.max(0, (page - 1) * limit);
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
    });
    const res = await this.request<unknown[]>(
      `${API_CONFIG.ENDPOINTS.ALERT_LOGS}?${params.toString()}`,
      {
        headers: token ? getAuthHeaders(token) : {},
      },
    );
    if (!res.success || !Array.isArray(res.data)) {
      return { success: false, data: [], error: 'Invalid alert log response' };
    }
    return { success: true, data: mapAlertLogsToTradingAlerts(res.data) };
  }

  // Webhook management endpoints
  async getWebhooks(token?: string): Promise<ApiResponse<WebhookMapping[]>> {
    return this.request(API_CONFIG.ENDPOINTS.WEBHOOKS, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async getWebhookMappings(token?: string): Promise<ApiResponse<WebhookMapping[]>> {
    const headers = token ? getAuthHeaders(token) : {};
    console.log('Request headers:', headers);
    const response = await this.request(API_CONFIG.ENDPOINTS.WEBHOOKS, {
      headers,
    });
    
    if (response.success && response.data && Array.isArray(response.data)) {
      // Transform backend format to frontend format
      const transformedData = response.data.map((item: any) => ({
        id: item.id.toString(),
        ticker: item.ticker,
        orderType: item.order_type,
        webhookUrl: item.webhook_url,
        secretWord: item.secret_word || '', // Use secret_word from backend
        status: (item.is_active ? 'active' : 'inactive') as 'active' | 'inactive' | 'error',
        lastTest: item.last_tested_at || new Date().toISOString(), // Use last_tested_at from backend
        last_tested_at: item.last_tested_at,
        last_test_status: item.last_test_status,
        last_test_execution_time: item.last_test_execution_time,
        createdAt: item.created_at
      }));
      
      return {
        success: true,
        data: transformedData
      };
    }
    
    return {
      success: false,
      data: []
    };
  }

  async createWebhookMapping(
    mapping: Omit<WebhookMapping, 'id' | 'createdAt' | 'lastTest' | 'status'>,
    token?: string
  ): Promise<ApiResponse<WebhookMapping>> {
    // Transform frontend format to backend format
    const backendMapping = {
      ticker: mapping.ticker,
      order_type: mapping.orderType,
      webhook_url: mapping.webhookUrl,
      secret_word: mapping.secretWord || 'default123', // Include secret_word field
      is_active: true,
      description: `Webhook for ${mapping.ticker} ${mapping.orderType}`
    };
    
    const response = await this.request(`${API_CONFIG.ENDPOINTS.WEBHOOKS}create`, {
      method: 'POST',
      body: JSON.stringify(backendMapping),
      headers: token ? getAuthHeaders(token) : {}
    });
    
    if (response.success && response.data && typeof response.data === 'object' && (response.data as any).id) {
      // Transform backend format to frontend format
      const data = response.data as any;
      const transformedData = {
        id: data.id.toString(),
        ticker: data.ticker,
        orderType: data.order_type,
        webhookUrl: data.webhook_url,
        secretWord: 'default123', // Backend doesn't have secretWord, use default
        status: (data.is_active ? 'active' : 'inactive') as 'active' | 'inactive' | 'error',
        lastTest: new Date().toISOString(), // Backend doesn't have lastTest
        createdAt: data.created_at
      };
      
      return {
        success: true,
        data: transformedData
      };
    }
    
    return {
      success: false,
      data: null as any
    };
  }

  async updateWebhookMapping(
    id: string,
    mapping: Partial<WebhookMapping>,
    token?: string
  ): Promise<ApiResponse<WebhookMapping>> {
    // Transform frontend format to backend format
    const backendMapping: any = {};
    if (mapping.ticker) backendMapping.ticker = mapping.ticker;
    if (mapping.orderType) backendMapping.order_type = mapping.orderType;
    if (mapping.webhookUrl) backendMapping.webhook_url = mapping.webhookUrl;
    if (mapping.secretWord !== undefined) backendMapping.secret_word = mapping.secretWord;
    if (mapping.status) backendMapping.is_active = mapping.status === 'active';
    
    const response = await this.request(`${API_CONFIG.ENDPOINTS.WEBHOOKS}${id}`, {
      method: 'PUT',
      body: JSON.stringify(backendMapping),
      headers: token ? getAuthHeaders(token) : {},
    });
    
    if (response.success && response.data && typeof response.data === 'object' && (response.data as any).id) {
      // Transform backend format to frontend format
      const data = response.data as any;
      const transformedData = {
        id: data.id.toString(),
        ticker: data.ticker,
        orderType: data.order_type,
        webhookUrl: data.webhook_url,
        secretWord: 'default123', // Backend doesn't have secretWord, use default
        status: (data.is_active ? 'active' : 'inactive') as 'active' | 'inactive' | 'error',
        lastTest: new Date().toISOString(), // Backend doesn't have lastTest
        createdAt: data.created_at
      };
      
      return {
        success: true,
        data: transformedData
      };
    }
    
    return {
      success: false,
      data: null as any
    };
  }

  async deleteWebhookMapping(id: string, token?: string): Promise<ApiResponse<void>> {
    return this.request(`${API_CONFIG.ENDPOINTS.WEBHOOKS}${id}`, {
      method: 'DELETE',
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async testWebhook(webhookUrl: string, token?: string): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return this.request(`${API_CONFIG.ENDPOINTS.WEBHOOK_TEST}`, {
      method: 'POST',
      body: JSON.stringify({ 
        webhook_url: webhookUrl,
        test_data: { ticker: "TEST", order_type: "test call" }
      }),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Alert Configuration endpoints
  async getAlertConfigurations(token?: string): Promise<ApiResponse<AlertConfiguration[]>> {
    const response = await this.request<AlertConfiguration[]>(`${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/`, {
      headers: token ? getAuthHeaders(token) : {},
    });

    if (response.success && response.data) {
      const transformedData = (response.data as any[]).map((config: any) => ({
        ...config,
        stock_symbol: config.stock_symbol ?? config.stockSymbol ?? '',
        position_type: config.position_type ?? config.positionType ?? '',
        secret_word: config.secret_word ?? config.secretWord ?? '',
        webhook_url: config.webhook_url ?? config.webhookUrl ?? '',
        is_active: config.is_active ?? config.isActive ?? false,
        created_at: config.created_at ?? config.createdAt,
        updated_at: config.updated_at ?? config.updatedAt,
        last_tested_at: config.last_tested_at ?? config.lastTestedAt ?? config.lastTest,
        last_test_status: config.last_test_status ?? config.lastTestStatus,
        last_test_execution_time: config.last_test_execution_time ?? config.lastTestExecutionTime ?? config.executionTime,
        last_triggered_at: config.last_triggered_at ?? config.lastTriggeredAt,
        incoming_webhook_token:
          config.incoming_webhook_token ?? config.incomingWebhookToken ?? null,
        alert_kind: config.alert_kind ?? config.alertKind,
        is_multi_trigger: config.is_multi_trigger ?? config.isMultiTrigger ?? false,
        trigger_count: config.trigger_count ?? config.triggerCount,
        slot_sources: config.slot_sources ?? config.slotSources ?? [],
        linked_directional_alert_id:
          config.linked_directional_alert_id ?? config.linkedDirectionalAlertId ?? null,
        linked_directional_slot_index:
          config.linked_directional_slot_index ?? config.linkedDirectionalSlotIndex ?? null,
        stockSymbol: config.stockSymbol ?? config.stock_symbol ?? '',
        positionType: (config.positionType ?? config.position_type ?? '') as any,
        secretWord: config.secretWord ?? config.secret_word ?? '',
        webhookUrl: config.webhookUrl ?? config.webhook_url ?? '',
        status: (config.is_active ?? config.isActive) ? 'active' : 'inactive' as 'active' | 'inactive' | 'error',
        createdAt: config.createdAt ?? config.created_at,
        lastTest: config.lastTest ?? config.last_tested_at,
        executionTime: config.executionTime ?? config.last_test_execution_time,
      }));

      return {
        success: true,
        data: transformedData
      };
    }
    
    // Return error response with correct type
    return {
      success: false,
      data: [],
      error: response.error || 'Failed to load alert configurations'
    };
  }

  async createAlertConfiguration(configData: any, token?: string): Promise<ApiResponse<AlertConfiguration>> {
    return this.request(`${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/`, {
      method: 'POST',
      body: JSON.stringify(configData),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async updateAlertConfiguration(id: string, configData: any, token?: string): Promise<ApiResponse<AlertConfiguration>> {
    return this.request(`${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(configData),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async deleteAlertConfiguration(id: string, token?: string): Promise<ApiResponse<void>> {
    return this.request(`${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${id}`, {
      method: 'DELETE',
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async testAlertConfiguration(id: string, webhookUrl: string, token?: string): Promise<ApiResponse<{ status: string; execution_time_ms: number; response_data: any; error_message: string | null }>> {
    // Use the alert configuration specific test endpoint which updates the database
    return this.request(`${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${id}/test`, {
      method: 'POST',
      body: JSON.stringify({
        webhook_url: webhookUrl,
        test_data: { ticker: "TEST", order_type: "test call" }
      }),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Performance and metrics endpoints
  async getPerformanceMetrics(token?: string): Promise<ApiResponse<PerformanceMetrics>> {
    return this.request(API_CONFIG.ENDPOINTS.PERFORMANCE, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async getMetrics(token?: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.METRICS, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Safety controls endpoints
  async getSafetyStatus(token?: string): Promise<ApiResponse<{ emergencyStop: boolean; lastStop?: string }>> {
    return this.request(API_CONFIG.ENDPOINTS.SAFETY_STATUS, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async triggerEmergencyStop(
    reason: string,
    token: string
  ): Promise<ApiResponse<{ success: boolean; message: string }>> {
    return this.request(API_CONFIG.ENDPOINTS.EMERGENCY_STOP, {
      method: 'POST',
      body: JSON.stringify({ reason }),
      headers: getAuthHeaders(token),
    });
  }

  // Configuration endpoints
  async getConfig(token?: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.CONFIG, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async getEmailConfig(token?: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_CONFIG, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async updateEmailConfig(
    config: any,
    token: string
  ): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_CONFIG, {
      method: 'PUT',
      body: JSON.stringify(config),
      headers: getAuthHeaders(token),
    });
  }

  // User registration
  async registerUser(userData: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<ApiResponse<{ token: string; user: any }>> {
    // Convert frontend field names to backend expected format
    // Generate a valid username from email (take part before @, replace invalid chars)
    const emailUsername = userData.email.split('@')[0].replace(/[^a-zA-Z0-9_-]/g, '_');
    // Ensure username meets minimum length requirement (3 chars)
    const username = emailUsername.length >= 3 ? emailUsername : emailUsername + '123';
    
    const registrationData = {
      email: userData.email,
      password: userData.password,
      first_name: userData.firstName,
      last_name: userData.lastName,
      username: username, // Generate valid username from email
    };
    return this.request(API_CONFIG.ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(registrationData),
    });
  }

  // Dashboard endpoints
  async getDashboardOverview(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.DASHBOARD_OVERVIEW, {
      headers: getAuthHeaders(token),
    });
  }

  async getDashboardConfig(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.DASHBOARD_CONFIG, {
      headers: getAuthHeaders(token),
    });
  }

  async getDashboardWidgets(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.DASHBOARD_WIDGETS, {
      headers: getAuthHeaders(token),
    });
  }

  // Admin endpoints
  async getAdminDashboard(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ADMIN_DASHBOARD, {
      headers: getAuthHeaders(token),
    });
  }

  async getAdminUsers(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ADMIN_USERS, {
      headers: getAuthHeaders(token),
    });
  }

  async getAdminSystemConfig(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ADMIN_SYSTEM_CONFIG, {
      headers: getAuthHeaders(token),
    });
  }

  async getAdminAuditLogs(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ADMIN_AUDIT_LOGS, {
      headers: getAuthHeaders(token),
    });
  }

  async getAdminSecurityEvents(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.ADMIN_SECURITY_EVENTS, {
      headers: getAuthHeaders(token),
    });
  }

  // Email management endpoints
  async getEmailAddresses(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES, {
      headers: getAuthHeaders(token),
    });
  }

  /** Get the signed-in user's email alias(es) on @branchrouter.com. */
  async getMyEmailAddresses(token?: string): Promise<ApiResponse<{
    status: string;
    email_addresses: Array<{
      id: string;
      email_address: string;
      local_part: string;
      domain: string;
      is_active: boolean;
      is_primary: boolean;
      provider?: string | null;
      provider_status?: string | null;
      created_at?: string;
    }>;
  }>> {
    return this.request(`${API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES}/me`, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Provision (or fetch existing) the signed-in user's alias on demand. */
  async generateMyEmailAddress(token?: string): Promise<ApiResponse<{
    email_address: string;
    local_part: string;
    domain: string;
  }>> {
    return this.post(`${API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES}/me/generate`, undefined, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Rotate the signed-in user's alias (deactivates the old one). */
  async regenerateMyEmailAddress(
    reason?: string,
    token?: string,
  ): Promise<ApiResponse<{ old_email: string; new_email: string; message: string }>> {
    return this.post(`${API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES}/me/regenerate`, { reason }, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Inbound emails received on the signed-in user's alias. */
  async getMyIncomingEmails(
    token?: string,
    limit = 50,
  ): Promise<ApiResponse<{
    status: string;
    incoming_emails: Array<{
      id: string;
      from_address: string;
      subject: string;
      body?: string;
      received_at: string;
      processed: boolean;
      confidence_score?: number | null;
      alert_id?: string | null;
      execution_time_ms?: number | null;
      execution_status: string;
    }>;
  }>> {
    return this.request(`${API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES}/me/incoming?limit=${limit}`, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Register the signed-in user's alias on Zoho Mail (retry after local-only create). */
  async syncMyEmailProvider(token?: string): Promise<ApiResponse<{
    status: string;
    email_address: string;
    provider_status: string;
    message: string;
  }>> {
    return this.post(`${API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES}/me/sync-provider`, undefined, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async createEmailAddress(emailData: any, token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_ADDRESSES, {
      method: 'POST',
      body: JSON.stringify(emailData),
      headers: getAuthHeaders(token),
    });
  }

  async getEmailDomains(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_DOMAINS, {
      headers: getAuthHeaders(token),
    });
  }

  async getEmailServers(token: string): Promise<ApiResponse<any>> {
    return this.request(API_CONFIG.ENDPOINTS.EMAIL_SERVERS, {
      headers: getAuthHeaders(token),
    });
  }

  // Alert webhook endpoints (multiple webhooks per alert)
  async getAlertWebhooks(alertId: number, token?: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/alert-webhooks/alert/${alertId}/webhooks`, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async createAlertWebhook(alertId: number, webhookData: {
    webhook_url: string;
    webhook_name?: string;
    is_active?: boolean;
    is_primary?: boolean;
    description?: string;
    custom_payload?: string | null;
    bmta_execution_group?: 'up' | 'down' | 'break' | null;
    dmta_execution_group?: string | null;
  }, token?: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/alert-webhooks/alert/${alertId}/webhooks`, {
      method: 'POST',
      body: JSON.stringify(webhookData),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async createMultipleAlertWebhooks(alertId: number, webhooks: Array<{
    webhook_url: string;
    webhook_name?: string;
    is_active?: boolean;
    is_primary?: boolean;
    description?: string;
    custom_payload?: string | null;
    bmta_execution_group?: 'up' | 'down' | 'break' | null;
    dmta_execution_group?: string | null;
  }>, token?: string): Promise<ApiResponse<any[]>> {
    return this.request(`/api/v1/alert-webhooks/alert/${alertId}/webhooks/multiple`, {
      method: 'POST',
      body: JSON.stringify({ webhooks }),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async updateAlertWebhook(webhookId: number, webhookData: {
    webhook_url?: string;
    webhook_name?: string;
    is_active?: boolean;
    is_primary?: boolean;
    description?: string;
    custom_payload?: string | null;
    bmta_execution_group?: 'up' | 'down' | 'break' | null;
    dmta_execution_group?: string | null;
  }, token?: string): Promise<ApiResponse<any>> {
    return this.request(`/api/v1/alert-webhooks/webhook/${webhookId}`, {
      method: 'PUT',
      body: JSON.stringify(webhookData),
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  async deleteAlertWebhook(webhookId: number, token?: string): Promise<ApiResponse<void>> {
    return this.request(`/api/v1/alert-webhooks/webhook/${webhookId}`, {
      method: 'DELETE',
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** Reveal plaintext custom_payload after step-up re-authentication. */
  async revealAlertWebhookPayload(
    webhookId: number,
    stepUpToken: string,
    token?: string
  ): Promise<ApiResponse<{ webhook_id: number; custom_payload: string | null }>> {
    const headers: Record<string, string> = {
      ...(token ? getAuthHeaders(token) : {}),
      'X-Step-Up-Token': stepUpToken,
    };
    return this.request(`/api/v1/alert-webhooks/webhook/${webhookId}/reveal-payload`, {
      method: 'POST',
      headers,
    });
  }

  async testAlertWebhook(webhookId: number, token?: string): Promise<ApiResponse<{
    success: boolean;
    message: string;
    execution_time: number;
    response?: any;
  }>> {
    return this.request(`/api/v1/alert-webhooks/webhook/${webhookId}/test`, {
      method: 'POST',
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Subscription endpoints
  async getSubscription(token?: string): Promise<ApiResponse<{
    subscription_id: number;
    user_id: number;
    tier: 'starter' | 'pro' | 'elite' | 'ultimate' | 'basic' | 'standard' | 'enterprise' | 'trial' | 'desktop' | 'admin';
    status: 'active' | 'trial' | 'expired' | 'suspended' | 'cancelled' | 'admin';
    webhook_limit: number;
    /** Max multi-trigger slot count (3, 5, or 7) for this tier */
    max_trigger_count?: number;
    trial_start_date?: string | null;
    trial_end_date?: string | null;
    subscription_start_date?: string | null;
    subscription_end_date?: string | null;
    created_at: string;
    updated_at?: string | null;
  }>> {
    return this.request('/api/v1/subscriptions/current', {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /**
   * POST a new support ticket for the signed-in user. Any authenticated user can
   * file a ticket; admins triage them in the Admin Support Center.
   */
  async createSupportTicket(
    body: {
      subject: string;
      description: string;
      category: 'technical' | 'billing' | 'feature_request' | 'bug_report';
      priority?: 'low' | 'medium' | 'high' | 'urgent';
    },
    token?: string,
  ): Promise<ApiResponse<{ id: string; ticket_number: string; status: string }>> {
    return this.post('/api/v1/admin/support-tickets', body, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** GET the signed-in user's own support tickets (newest first). */
  async getMyTickets(token?: string): Promise<ApiResponse<Array<{
    id: string;
    ticket_number: string | null;
    subject: string;
    description: string;
    status: string;
    priority: string;
    category: string;
    resolution: string | null;
    created_at: string;
    updated_at: string | null;
  }>>> {
    return this.request('/api/v1/support/my-tickets', {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** GET the conversation thread for one of the user's tickets (no internal notes). */
  async getMyTicketMessages(ticketId: string, token?: string): Promise<ApiResponse<Array<{
    id: string;
    support_ticket_id: string;
    sender_type: string;
    message_content: string;
    created_at: string;
  }>>> {
    return this.request(`/api/v1/support/my-tickets/${ticketId}/messages`, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** POST a customer reply on their own ticket. */
  async replyToMyTicket(
    ticketId: string,
    messageContent: string,
    token?: string,
  ): Promise<ApiResponse<{
    id: string;
    support_ticket_id: string;
    sender_type: string;
    message_content: string;
    created_at: string;
  }>> {
    return this.post(
      `/api/v1/support/my-tickets/${ticketId}/messages`,
      { message_content: messageContent, is_internal: false },
      { headers: token ? getAuthHeaders(token) : {} },
    );
  }

  /** GET public payment config (provider + whether payments are enabled). */
  async getPaymentConfig(): Promise<ApiResponse<{ provider: string; enabled: boolean }>> {
    return this.request('/api/v1/payments/config');
  }

  /** GET the current Stripe subscription status for the signed-in user. */
  async getPaymentSubscription(token?: string): Promise<ApiResponse<{
    has_subscription: boolean;
    status?: string | null;
    tier?: string | null;
    billing_cycle?: string | null;
    current_period_end?: string | null;
    cancel_at_period_end: boolean;
    trial_end?: string | null;
  }>> {
    return this.request('/api/v1/payments/subscription', {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** POST start a Stripe Checkout session; returns the hosted checkout URL to redirect to. */
  async createCheckoutSession(
    body: {
      subscription_tier: string;
      billing_cycle?: 'monthly' | 'yearly';
      tax_use: 'personal' | 'business';
      success_url?: string;
    },
    token?: string,
  ): Promise<ApiResponse<{ checkout_url: string }>> {
    return this.post('/api/v1/payments/checkout', body, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** POST open the Stripe Customer Portal; returns the hosted portal URL to redirect to. */
  async createBillingPortalSession(
    returnUrl?: string,
    token?: string,
  ): Promise<ApiResponse<{ portal_url: string }>> {
    return this.post('/api/v1/payments/portal', { return_url: returnUrl }, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  /** GET trigger status for a multi-trigger alert (slot activation state). */
  async getTriggerStatus(alertConfigId: string | number, token?: string): Promise<ApiResponse<TriggerStatusResponse>> {
    return this.request<TriggerStatusResponse>(
      `${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${alertConfigId}/trigger-status`,
      { headers: token ? getAuthHeaders(token) : {} }
    );
  }

  /** POST reset BMTA slot(s) to neutral (omit slot_index to reset all). */
  async resetBidirectionalSlots(
    alertConfigId: string | number,
    body: { slot_index?: number },
    token?: string,
  ): Promise<ApiResponse<{ status: string; slot_directions: string[] }>> {
    return this.request(
      `${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${alertConfigId}/bidirectional-slots/reset`,
      {
        method: 'POST',
        headers: {
          ...(token ? getAuthHeaders(token) : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    );
  }

  /** POST reset DMTA slot(s) to inactive (omit slot_index to reset all). */
  async resetDirectionalSlots(
    alertConfigId: string | number,
    body: { slot_index?: number },
    token?: string,
  ): Promise<ApiResponse<{ status: string; active_slots: number[] }>> {
    return this.request(
      `${API_CONFIG.ENDPOINTS.ALERT_CONFIGURATIONS}/${alertConfigId}/directional-slots/reset`,
      {
        method: 'POST',
        headers: {
          ...(token ? getAuthHeaders(token) : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      },
    );
  }

  async getUsageStatistics(periodDays: number = 30, token?: string): Promise<ApiResponse<{
    current_usage: number;
    webhook_limit: number;
    usage_percentage: number;
    period_start: string;
    period_end: string;
    tier_at_time: string;
  }>> {
    return this.request(`/api/v1/subscriptions/usage?period_days=${periodDays}`, {
      headers: token ? getAuthHeaders(token) : {},
    });
  }

  // Incoming webhook trigger (token-based auth for external callers)
  /** GET current trigger token. Returns data: null when no token is set (404). */
  async getIncomingWebhookToken(token?: string): Promise<ApiResponse<{ token: string } | null>> {
    try {
      const res = await this.get<{ token: string }>(API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_TOKEN, {
        headers: token ? getAuthHeaders(token) : {},
      });
      return { success: true, data: res.data };
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        return { success: true, data: null };
      }
      throw e;
    }
  }

  /** POST to generate or rotate the incoming webhook token. Returns the new token. */
  async generateIncomingWebhookToken(token?: string): Promise<ApiResponse<{ token: string }>> {
    return this.post<{ token: string }>(
      API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_GENERATE_TOKEN,
      {},
      { headers: token ? getAuthHeaders(token) : {} }
    );
  }

  /** GET recent incoming webhook trigger attempts (JWT). */
  async getIncomingWebhookActivity(
    token?: string,
    limit = 100,
    offset = 0,
  ): Promise<ApiResponse<IncomingWebhookActivityRow[]>> {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return this.get<IncomingWebhookActivityRow[]>(
      `${API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ACTIVITY}?${qs.toString()}`,
      { headers: token ? getAuthHeaders(token) : {} },
    );
  }

  /** Reveal plaintext activity payload after step-up re-authentication. */
  async revealIncomingWebhookActivityPayload(
    activityId: number,
    stepUpToken: string,
    token?: string,
    kind: 'incoming' | 'outgoing' = 'incoming'
  ): Promise<
    ApiResponse<{
      activity_id: number;
      request_payload?: string | null;
      outgoing_payload?: string | null;
    }>
  > {
    const headers: Record<string, string> = {
      ...(token ? getAuthHeaders(token) : {}),
      'X-Step-Up-Token': stepUpToken,
    };
    const qs = new URLSearchParams({ kind });
    return this.request(
      `${API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ACTIVITY}/${activityId}/reveal-payload?${qs.toString()}`,
      { method: 'POST', headers }
    );
  }

  /** GET per-alert trigger token. Returns 404 if alert has no token yet. */
  async getAlertTriggerToken(alertId: number, authToken?: string): Promise<ApiResponse<{ token: string } | null>> {
    const path = typeof API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_TOKEN === 'function'
      ? API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_TOKEN(alertId)
      : `/api/v1/incoming-webhook/alert/${alertId}/token`;
    try {
      const res = await this.get<{ token: string }>(path, {
        headers: authToken ? getAuthHeaders(authToken) : {},
      });
      return { success: true, data: res.data };
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        const detail = this.formatErrorDetail(e);
        return {
          success: true,
          data: null,
          message: formatRecentAlertMessage(detail) ?? MISSING_TRIGGER_WEBHOOK_MESSAGE,
        };
      }
      throw e;
    }
  }

  /** POST to generate or rotate the per-alert trigger token. No request body needed when triggering this alert. */
  async generateAlertTriggerToken(alertId: number, authToken?: string): Promise<ApiResponse<{ token: string }>> {
    const path = typeof API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_GENERATE_TOKEN === 'function'
      ? API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_GENERATE_TOKEN(alertId)
      : `/api/v1/incoming-webhook/alert/${alertId}/generate-token`;
    return this.post<{ token: string }>(
      path,
      {},
      { headers: authToken ? getAuthHeaders(authToken) : {} }
    );
  }

  /** GET per-slot trigger credential(s). BMTA returns tokenUp + tokenDown; others return token. */
  async getAlertSlotTriggerToken(
    alertId: number,
    slotIndex: number,
    authToken?: string
  ): Promise<
    ApiResponse<{ token?: string | null; tokenUp?: string | null; tokenDown?: string | null } | null>
  > {
    const path = typeof API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_SLOT_TOKEN === 'function'
      ? API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_SLOT_TOKEN(alertId, slotIndex)
      : `/api/v1/incoming-webhook/alert/${alertId}/slot/${slotIndex}/token`;
    try {
      const res = await this.get<{ token?: string | null; tokenUp?: string | null; tokenDown?: string | null }>(
        path,
        {
        headers: authToken ? getAuthHeaders(authToken) : {},
      }
      );
      return { success: true, data: res.data };
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        const detail = this.formatErrorDetail(e);
        return {
          success: true,
          data: null,
          message: formatRecentAlertMessage(detail) ?? MISSING_TRIGGER_WEBHOOK_MESSAGE,
        };
      }
      throw e;
    }
  }

  /** POST to generate or rotate per-slot trigger credential(s). BMTA returns tokenUp + tokenDown. */
  async generateAlertSlotTriggerToken(
    alertId: number,
    slotIndex: number,
    authToken?: string
  ): Promise<ApiResponse<{ token?: string | null; tokenUp?: string | null; tokenDown?: string | null }>> {
    const path = typeof API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_SLOT_GENERATE_TOKEN === 'function'
      ? API_CONFIG.ENDPOINTS.INCOMING_WEBHOOK_ALERT_SLOT_GENERATE_TOKEN(alertId, slotIndex)
      : `/api/v1/incoming-webhook/alert/${alertId}/slot/${slotIndex}/generate-token`;
    return this.post<{ token?: string | null; tokenUp?: string | null; tokenDown?: string | null }>(
      path,
      {},
      { headers: authToken ? getAuthHeaders(authToken) : {} }
    );
  }

}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
